// Waiting copy, in the order it escalates. A turn's median is well under five
// seconds, so the calm default carries almost every turn; the later tiers only
// ever show on the turns that genuinely ran long, which is exactly when a
// static label reads as a stall. Plain and finite on purpose: no promise about
// how much longer it will be, since nothing here knows.
export const WAITING_LABELS = [
  { afterMs: 0, label: "Working on it" },
  { afterMs: 8000, label: "Still going, this one is taking a moment" },
  { afterMs: 20000, label: "Still working, thanks for waiting" },
];

export const STEP_FALLBACK_LABEL = WAITING_LABELS[0].label;

export const waitingLabel = (elapsedMs) => {
  const elapsed = isMs(elapsedMs) ? elapsedMs : 0;
  let label = WAITING_LABELS[0].label;
  for (const tier of WAITING_LABELS) {
    if (elapsed >= tier.afterMs) label = tier.label;
  }
  return label;
};

// What to say while nothing specific is happening. Between tool calls the
// model is composing, and there is no honest specific to report, so these
// rotate rather than repeating one line at someone for twelve seconds.
// Deliberately vague about WHAT is being read: naming a source we might not
// have touched on this turn would be a small lie told very confidently, and
// the whole point of this panel is that it reports rather than performs.
export const THINKING_LABELS = [
  "Thinking it through",
  "Working out what you need",
  "Pulling that together",
  "Checking the details",
];

export const ROTATE_EVERY_MS = 2600;

// The line to show right now. A running step always wins: it is the one
// thing here that is literally true, and it is specific ("Looking up that
// property at 307 Redwood Avenue") in a way no generic phrase can be. The
// rotation only fills the gaps between steps.
//
// A step is "running" at status "start" - applyStepEvent stores the wire
// status verbatim, and the only in-flight value the stream (or StepIcon)
// ever uses is "start"; "running" is not a status this pipeline produces.
export const activityLine = (steps, elapsedMs) => {
  const elapsed = isMs(elapsedMs) ? elapsedMs : 0;
  const list = Array.isArray(steps) ? steps : [];
  const running = [...list].reverse().find((s) => s && s.status === "start");
  if (running) {
    const detail = text(running.detail);
    const label = text(running.label) || STEP_FALLBACK_LABEL;
    const alreadyIncludesDetail =
      detail && label.toLowerCase().endsWith(detail.toLowerCase());
    return {
      text: detail && !alreadyIncludesDetail ? `${label}: ${detail}` : label,
      key: `step:${running.id}`,
    };
  }
  // Past the escalation tiers the reassurance matters more than variety, so
  // it stops rotating and holds the tier line.
  if (elapsed >= WAITING_LABELS[1].afterMs) {
    return { text: waitingLabel(elapsed), key: `tier:${waitingLabel(elapsed)}` };
  }
  const index = Math.floor(elapsed / ROTATE_EVERY_MS) % THINKING_LABELS.length;
  return { text: THINKING_LABELS[index], key: `think:${index}` };
};

const isMs = (value) =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const text = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

export const applyStepEvent = (steps, event) => {
  const list = Array.isArray(steps) ? steps : [];
  if (!event || typeof event.id !== "string" || typeof event.status !== "string") {
    return list;
  }
  const index = list.findIndex((step) => step.id === event.id);
  if (event.status === "start" && index !== -1) return list;

  const incoming = {
    label: text(event.label),
    detail: text(event.detail),
    result: text(event.result),
    ms: isMs(event.ms) ? event.ms : null,
  };

  if (index === -1) {
    return [
      ...list,
      {
        id: event.id,
        status: event.status,
        label: incoming.label || STEP_FALLBACK_LABEL,
        detail: incoming.detail,
        result: incoming.result,
        ms: incoming.ms,
      },
    ];
  }

  const previous = list[index];
  const next = [...list];
  next[index] = {
    ...previous,
    status: event.status,
    label: incoming.label || previous.label,
    detail: incoming.detail === null ? previous.detail : incoming.detail,
    result: incoming.result === null ? previous.result : incoming.result,
    ms: incoming.ms === null ? previous.ms : incoming.ms,
  };
  return next;
};

export const formatElapsed = (ms) => {
  const seconds = isMs(ms) ? Math.floor(ms / 1000) : 0;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
};

export const formatStepDuration = (ms) => {
  if (!isMs(ms)) return null;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 10000) return `${(ms / 1000).toFixed(1)}s`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

export const summarizeWork = (steps, totalMs) => {
  const count = Array.isArray(steps) ? steps.length : 0;
  const stepLabel = `${count} step${count === 1 ? "" : "s"}`;
  const duration = formatStepDuration(totalMs);
  return duration ? `Worked for ${duration}, ${stepLabel}` : stepLabel;
};

export const hasFailedStep = (steps) =>
  (Array.isArray(steps) ? steps : []).some((step) => step.status === "error");
